"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";
import { Input } from "@/shared/ui/inputs/input";
import { Label } from "@/shared/ui/forms/label";
import { api } from "@/shared/lib/api";
import { useTranslations } from "next-intl";

const PRESET_AMOUNTS = [50, 100, 300, 500, 1000];
const MIN_AMOUNT = 10;
const MAX_AMOUNT = 150_000;

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.08 },
  },
};

const item = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

type DonateModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function DonateModal({ open, onOpenChange }: DonateModalProps) {
  const t = useTranslations("Marketing.About.donate");
  const [amount, setAmount] = useState("100");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedAmount = Number.parseFloat(amount.replace(",", "."));
  const isValidAmount =
    !Number.isNaN(parsedAmount) &&
    parsedAmount >= MIN_AMOUNT &&
    parsedAmount <= MAX_AMOUNT;

  const handlePay = async () => {
    if (!isValidAmount) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ paymentUrl?: string; error?: string }>(
        "/marketing/donate-init",
        { amountRubles: parsedAmount },
      );
      const data = res.data;
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
        return;
      }
      setError(data?.error ?? t("errorGeneric"));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
        ?? (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden rounded-2xl border border-border bg-background p-0 shadow-xl sm:max-w-md md:max-w-lg"
        showCloseButton
      >
        <div className="relative px-6 pt-6 pb-1 sm:px-8 sm:pt-8">
          <div className="absolute top-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-primary/30 to-transparent" />
          <DialogHeader className="relative space-y-1 pb-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"
            >
              <Coins className="h-6 w-6" />
            </motion.div>
            <DialogTitle className="text-xl font-semibold tracking-tight sm:text-2xl">
              {t("modalTitle")}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground sm:text-base">
              {t("modalDesc")}
            </DialogDescription>
          </DialogHeader>

          <motion.div
            variants={container}
            initial="hidden"
            animate="visible"
            className="flex flex-col gap-5 py-2"
          >
            <motion.div variants={item} className="space-y-3">
              <Label className="text-sm font-medium" htmlFor="donate-amount">
                {t("amountLabel")}
              </Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {PRESET_AMOUNTS.map((a) => (
                  <motion.div key={a} variants={item}>
                    <Button
                      type="button"
                      variant={parsedAmount === a ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAmount(String(a))}
                      className="h-10 w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      {a} ₽
                    </Button>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={item} className="space-y-2">
              <Label htmlFor="donate-amount" className="sr-only">
                {t("amountLabel")}
              </Label>
              <Input
                id="donate-amount"
                type="text"
                inputMode="decimal"
                placeholder="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-invalid={!isValidAmount && amount !== ""}
                className="h-11 rounded-xl border-2 transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/20"
              />
            </motion.div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.span
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="block text-sm text-destructive"
                >
                  {error}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        <DialogFooter className="flex flex-row flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-4 sm:px-8 sm:py-5 dark:bg-muted/30">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl transition-colors"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handlePay}
            disabled={loading || !isValidAmount}
            className="min-w-[140px] rounded-xl bg-primary shadow-md transition-all duration-200 hover:shadow-lg hover:shadow-primary/25 disabled:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("loading")}
              </>
            ) : (
              t("pay")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
